import { useState } from 'react';
import { credentials } from '../lib/api';
import { useApi } from '../hooks/useApi';
import { useTranslation, type TKey } from '../i18n';
import { Badge, Button, EmptyState, Sidenote } from '../components/ui';
import { toast } from 'sonner';

type Tier = 'micro' | 'standard' | 'major' | 'master' | 'legendary';

const TIER_ORDER: Tier[] = ['micro', 'standard', 'major', 'master', 'legendary'];

const TIER_KEY: Record<Tier, TKey> = {
  micro:     'creds.tier.micro',
  standard:  'creds.tier.standard',
  major:     'creds.tier.major',
  master:    'creds.tier.master',
  legendary: 'creds.tier.legendary',
};

const TIER_MARK: Record<Tier, string> = {
  micro:     '·',
  standard:  '◆',
  major:     '★',
  master:    '★★',
  legendary: '✦',
};

export default function CredentialsPage() {
  const { t } = useTranslation();
  const state = useApi(() => credentials.me(), []);
  const items = state.status === 'success' ? state.data.items : [];
  const summaryState = useApi(() => credentials.summary(), []);
  const summary = summaryState.status === 'success' ? summaryState.data : null;

  const counts: Record<Tier, number> = { micro: 0, standard: 0, major: 0, master: 0, legendary: 0 };
  for (const c of items) counts[c.tier as Tier] = (counts[c.tier as Tier] ?? 0) + 1;

  const [copiedUrn, setCopiedUrn] = useState<string | null>(null);

  async function shareCredential(urn: string) {
    const url = `${window.location.origin}/verify?urn=${encodeURIComponent(urn)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'BookQuest', url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t('creds.copied'));
      }
    } catch { /* user cancelled share — no-op */ }
  }

  async function copyUrn(urn: string) {
    try {
      await navigator.clipboard.writeText(urn);
      setCopiedUrn(urn);
      window.setTimeout(() => {
        setCopiedUrn((cur) => (cur === urn ? null : cur));
      }, 1800);
    } catch {
      toast.error(t('creds.copyFailed'));
    }
  }

  return (
    <div className="container">
      <section className="cred-hero cred-hero--v2">
        <div>
          <div className="kicker cred-hero-kicker">
            {t('creds.kicker')} · {items.length} {t('creds.count')}
          </div>
          <h1 className="cred-hero-title">
            {t('creds.heroLine')} <em>{t('creds.heroEm')}</em>
          </h1>
          <p className="cred-hero-lead">
            {t('creds.heroSub')}
          </p>

          {summary && summary.total > 0 && (
            <div className="cred-summary">
              <span className="cred-summary__label">{t('creds.summaryLabel')}</span>
              <div className="cred-summary__stats">
                <span><strong>{summary.total}</strong> {t('creds.summaryTotal')}</span>
                <span><strong>{summary.books}</strong> {t('creds.summaryBooks')}</span>
                <span>
                  {t('creds.summaryHighest')}:{' '}
                  <strong>
                    {summary.highest_tier
                      ? (summary.highest_tier === 'master'
                          ? t('creds.master')
                          : t(TIER_KEY[summary.highest_tier as Tier]))
                      : '—'}
                  </strong>
                </span>
              </div>
            </div>
          )}

          <div className="cred-counts">
            {TIER_ORDER.map((tier) => (
              <div key={tier} className={`cc cc--${tier}`}>
                <div className="cc-label">
                  {tier === 'master' ? t('creds.master') : t(TIER_KEY[tier])}
                </div>
                <div className="cc-value">
                  {counts[tier] === 0 ? '—' : String(counts[tier]).padStart(2, '0')}
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside className="cred-hero__aside">
          <Sidenote label={t('creds.aside.tiersLabel')} marker="★">
            <p>{t('creds.aside.tiersBody')}</p>
          </Sidenote>
          <Sidenote label={t('creds.aside.verifyLabel')} tone="accent">
            <p>{t('creds.aside.verifyBody')}</p>
          </Sidenote>
          <Sidenote label={t('creds.aside.standardLabel')} tone="mute">
            <p className="mono">W3C VC · OpenBadges 3.0</p>
          </Sidenote>
        </aside>
      </section>

      <section>
        <div className="sec-head">
          <h2>{t('creds.recent')}</h2>
          <span className="meta">{state.status === 'loading' ? t('common.loading') : `${items.length}`}</span>
        </div>

        {state.status === 'loading' && (
          <div className="cred-grid" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="cred-card cred-card--skeleton">
                <div className="cred-card__strip cred-card__strip--skeleton" />
                <div className="skeleton skeleton-line w-40 mt-8" />
                <div className="skeleton skeleton-line w-60 mt-16" />
                <div className="skeleton skeleton-line w-30 mt-4" />
                <div className="cred-card__skel-spacer" />
                <div className="skeleton skeleton-line w-50" />
                <div className="skeleton skeleton-line w-40 mt-2" />
              </div>
            ))}
          </div>
        )}
        {state.status === 'error' && (
          <EmptyState
            tone="error"
            title={t('common.offline')}
            description={t('creds.noneYet')}
          />
        )}
        {state.status === 'success' && items.length === 0 && (
          <EmptyState
            title={t('creds.noneYet')}
            description={t('onboard.welcomeSub')}
          />
        )}

        {items.length > 0 && (
          <div className="cred-grid">
            {items.map((c) => {
              const tier = c.tier as Tier;
              const issuedMs = c.issued_at ? Date.parse(c.issued_at) : 0;
              const isNew = issuedMs > 0 && (Date.now() - issuedMs) < 5 * 60 * 1000;
              return (
                <article
                  key={c.id}
                  className={`bq-card bq-card--credential cred-card cred-card--${tier}${isNew ? ' cred-card--new' : ''}`}
                >
                  {isNew && (
                    <span className="cred-card__new-badge" aria-label={t('creds.justIssued')}>
                      {t('creds.justIssued')}
                    </span>
                  )}
                  <div className="cred-card__strip" />

                  <header className="cred-card__head">
                    <Badge variant="soft" tone={`tier-${tier}`} size="sm">
                      {tier === 'master' ? t('creds.master') : t(TIER_KEY[tier])}
                    </Badge>
                    {c.anchored_at && (
                      <Badge variant="outline" tone="ink" size="sm">⚓ {t('creds.anchor')}</Badge>
                    )}
                  </header>

                  <div className="cred-card__mark" aria-hidden="true">{TIER_MARK[tier]}</div>

                  <div className="cred-card__title-block">
                    <h3 className="cred-card__name">{c.credential_type}</h3>
                    <p className="cred-card__sub">{(c.trigger_kind ?? '').replace(/_/g, ' ')}</p>
                  </div>

                  <dl className="cred-card__meta">
                    <div>
                      <dt>{t('creds.urn')}</dt>
                      <dd>
                        <button
                          type="button"
                          className={`cred-card__urn-btn${copiedUrn === c.credential_urn ? ' is-copied' : ''}`}
                          onClick={() => copyUrn(c.credential_urn)}
                          title={c.credential_urn}
                          aria-live="polite"
                        >
                          <span className="cred-card__urn-label">
                            {c.credential_urn.slice(-16)}
                          </span>
                          {copiedUrn === c.credential_urn && (
                            <span className="cred-card__urn-confirm" aria-hidden="true">
                              ✓ {t('creds.copied')}
                            </span>
                          )}
                        </button>
                      </dd>
                    </div>
                    <div>
                      <dt>{t('creds.issued')}</dt>
                      <dd>{c.issued_at ? new Date(c.issued_at).toISOString().slice(0, 10) : '—'}</dd>
                    </div>
                  </dl>

                  <footer className="cred-card__foot">
                    <Button variant="link" size="sm" onClick={() => copyUrn(c.credential_urn)}>
                      {copiedUrn === c.credential_urn ? `✓ ${t('creds.copied')}` : t('creds.share')}
                    </Button>
                    <Button variant="link" size="sm" asChild>
                      <a href={`/verify?urn=${encodeURIComponent(c.credential_urn)}`}
                         target="_blank" rel="noopener noreferrer">{t('creds.verifyExternally')}</a>
                    </Button>
                    <Button variant="link" size="sm" onClick={() => shareCredential(c.credential_urn)}>
                      {t('creds.shareNative')}
                    </Button>
                    <Button variant="link" size="sm" onClick={() => window.print()}>
                      {t('creds.print')}
                    </Button>
                  </footer>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
